import { correlationIdHeader, genCorrelationId } from "@sp-crm/core";
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { fancyAlert } from "components/ui/fancy-alert";
import Debug from "debug";
import { RequestState } from "../constants/loading";
import { Actions } from "../store/actions";
import {
    makeRequestFailAction,
    makeRequestUnauthorizedAction,
} from "../store/actions/types";
import { RequestDescription } from "../types/request-description";
import { extraRequestHeaders } from "./browser";
import { reportError } from "./error-tracking";
import { push } from "./history";
import { checkVersionHeaderFromAxios } from "./version-check";

const debug = Debug("placement-crm.http");

const isNetworkError = (error: Error | null): boolean => {
    const typedError: Error | null = error as Error;
    const errorMessage = typedError?.message?.toLowerCase();

    return (
        errorMessage === "network error" ||
        errorMessage === "timeout of 0ms exceeded" ||
        errorMessage === "timeout exceeded" ||
        errorMessage?.includes("request aborted") ||
        errorMessage?.includes("requestaborted")
    );
};

export class ClientHttpNetworkError extends Error {
    constructor(public readonly originalError: Error) {
        super(`Network error: ${originalError?.message || "Fetch error"}`);
        this.name = "ClientHttpNetworkError";
    }
}

export class ClientTrpcNetworkError extends Error {
    public readonly skipReporting = true;

    constructor(public readonly originalError: Error) {
        super(`Network error: ${originalError?.message || "Fetch error"}`);
        this.name = "ClientTrpcNetworkError";
    }
}

export const ensureResponseCompleted = <T>(
    response: AxiosResponse<T>,
): AxiosResponse<T> => {
    if (response.status === 0) {
        throw new ClientHttpNetworkError(
            new Error("Request terminated by the browser before a response arrived"),
        );
    }
    return response;
};

const makeAxiosRequest = <T>(options: AxiosRequestConfig): AxiosPromise<T> => {
    return axios<T>(options)
        .then(response => {
            ensureResponseCompleted(response);
            checkVersionHeaderFromAxios(response.headers as Record<string, string>);
            return response;
        })
        .catch(error => {
            if (isNetworkError(error)) {
                throw new ClientHttpNetworkError(error);
            }

            throw error;
        });
};

export function send(
    requestDescription: RequestDescription,
    method: string,
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    body?: any,
): AxiosPromise {
    const options = {
        url: path,
        method,
        headers: {
            ...extraRequestHeaders(),
            Accept: "application/json",
            "Content-Type": "application/json",
            [correlationIdHeader]: requestDescription.correlationId,
        },
    };
    if (body) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
        (options as any)["data"] = body;
    }
    const promise = makeAxiosRequest(options);
    return promise;
}

export function get<T>(
    requestDescription: RequestDescription,
    path: string,
): AxiosPromise<T> {
    const promise = makeAxiosRequest<T>({
        url: path,
        method: "GET",
        headers: {
            ...extraRequestHeaders(),
            Accept: "application/json",
            "Content-Type": "application/json",
            [correlationIdHeader]: requestDescription.correlationId,
        },
    });
    return promise;
}

export function post<T>(
    requestDescription: RequestDescription,
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    body: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    headers: any = {},
): AxiosPromise<T> {
    const options = {
        url: path,
        method: "POST",
        headers: {
            ...extraRequestHeaders(),
            Accept: "application/json",
            "Content-Type": "application/json",
            [correlationIdHeader]: requestDescription.correlationId,

            ...headers,
        },
        data: body,
    };
    const promise = makeAxiosRequest<T>(options);
    return promise;
}

export function deleteReq(
    requestDescription: RequestDescription,
    path: string,
): AxiosPromise {
    const options = {
        method: "DELETE",
        headers: {
            ...extraRequestHeaders(),
            Accept: "application/json",
            "Content-Type": "application/json",
            [correlationIdHeader]: requestDescription.correlationId,
        },
        url: path,
    };
    const promise = makeAxiosRequest(options);
    return promise;
}

export function put(
    requestDescription: RequestDescription,
    path: string,
    body: unknown,
): AxiosPromise {
    const options = {
        method: "PUT",
        headers: {
            ...extraRequestHeaders(),
            Accept: "application/json",
            "Content-Type": "application/json",
            [correlationIdHeader]: requestDescription.correlationId,
        },
        data: body,
        url: path,
    };
    const promise = makeAxiosRequest(options);
    return promise;
}

class ClientRequestError extends Error {
    readonly name = "ClientRequestError";
}

export function errorHandler(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    requestOrError: any,
    requestDescription: RequestDescription,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- legacy dispatch type
    dispatch: Function,
): void {
    if (
        isNetworkError(requestOrError) ||
        requestOrError?.name === "ClientHttpNetworkError"
    ) {
        // 'Network Error' comes from https://github.com/axios/axios/blob/v1.x/lib/adapters/xhr.js#L151
        // TODO upgrade axios to get a better type on the exception
        dispatch(
            makeRequestFailAction(
                failedRequestDescription(requestDescription, {
                    msg: "Network error",
                    correlationId: "Network error",
                }),
            ),
        );
    } else if (requestOrError && requestOrError.response) {
        const response: AxiosResponse = requestOrError.response;
        const failedRequestInfo: FailedRequestInfo = {
            msg: requestOrError.response.data?.msg,
            correlationId: requestOrError.response.data?.correlationId,
            url: requestOrError.response?.config?.url || requestOrError.config?.url,
            method:
                requestOrError.response?.config?.method || requestOrError.config?.method,
        };

        if (response.status >= 500) {
            const payload = failedRequestDescription(
                requestDescription,
                failedRequestInfo,
            );
            if (response.data.customError) {
                fancyAlert("Error", response.data.customError);
            } else {
                dispatch(makeRequestFailAction(payload));
                debug("errorHandler 500 %o", requestOrError.response);
                const details = payload.details || "(not available)";
                const errorMessage = `${payload.method}:${payload.route} - ${details}`;
                let exception: Error;
                try {
                    throw new ClientRequestError(errorMessage);
                } catch (e) {
                    exception = e;
                }
                reportError(exception, {
                    fingerprint: `${payload.method}:${payload.route}`,
                    params: {
                        route: payload.route,
                        method: payload.method,
                        url: payload.url,
                        details: payload.details,
                        status: response.status,
                    },
                    context: {
                        correlationId: payload.correlationId,
                    },
                });
            }
        } else if (response.status === 404) {
            fancyAlert(
                "Record not found",
                'Sorry, the record you are accessing does not exist (or it was deleted). Try clicking "back" or close this tab.',
            );
        } else if (response.status === 413) {
            const responseMessage = response.data.msg as string | undefined;
            if (responseMessage?.includes("file count")) {
                fancyAlert(
                    "Too many files",
                    "Sorry, you can only upload up to 10 files at a time",
                );
            } else {
                fancyAlert(
                    "File too large",
                    "Sorry, the file you are uploading is too large. Files must be under 10 Megabytes",
                );
            }
        } else if (response.status === 422) {
            fancyAlert(
                "Please review your data",
                response.data?.customError ||
                    "Sorry, we weren't able to understand the last update. Please try updating again.",
            );
        } else if (response.status === 400) {
            fancyAlert(
                "Unable to save",
                response.data?.error ||
                    "Something went wrong. Please contact support if this continues.",
            );
        } else if (response.status === 403) {
            const payload = failedRequestDescription(
                requestDescription,
                failedRequestInfo,
            );
            dispatch(makeRequestUnauthorizedAction(payload));
        } else if (response.status === 401) {
            debug("errorHandler 401 %o", requestOrError.response);
            dispatch({ type: Actions[Actions.LOGOUT] });
            push("/login");
        } else {
            debug("errorHandler !500,!401 %o", requestOrError.response);
        }
    } else {
        // not a request, throw!
        throw requestOrError;
    }
}

interface FailedRequestInfo {
    msg?: string;
    correlationId?: string;
    url?: string;
    method?: string;
}

export function newRequestDescription(route: string) {
    const correlationId = genCorrelationId("client");
    const requestDescription: RequestDescription = {
        started: new Date(),
        ended: null,
        status: RequestState.NOTSTARTED,
        correlationId,
        route,
    };
    return requestDescription;
}

export function failedRequestDescription(
    newRequestDescription: RequestDescription,
    failedRequestInfo: FailedRequestInfo,
) {
    const requestDescription: RequestDescription = {
        ...newRequestDescription,
        ended: new Date(),
        status: RequestState.ERROR,
        correlationId:
            failedRequestInfo.correlationId || newRequestDescription.correlationId,
        details: failedRequestInfo.msg,
        eventType: "failedRequest",
        url: failedRequestInfo.url,
        method: failedRequestInfo.method,
    };
    return requestDescription;
}

const REQUEST_TIMEOUT_STATUS = 408;

export type ClientHttpEndpoint = "GraphQL" | "tRPC";

const httpErrorMessage = (
    statusCode: number,
    statusText: string,
    endpoint: ClientHttpEndpoint,
): string =>
    [`HTTP ${statusCode}`, statusText, `from ${endpoint} endpoint`]
        .filter(part => part.length > 0)
        .join(" ");

export class ClientHttpError extends Error {
    public readonly skipReporting: boolean;

    constructor(
        public readonly correlationId: string,
        public readonly statusCode: number,
        public readonly statusText: string,
        public readonly responseBody: string,
        public readonly endpoint: ClientHttpEndpoint,
    ) {
        super(httpErrorMessage(statusCode, statusText, endpoint));
        this.name = "ClientHttpError";
        this.skipReporting = statusCode === REQUEST_TIMEOUT_STATUS;
    }
}
